import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { SEO, breadcrumbSchema, faqSchema } from '../components/SEO';
import CTASection, { InlineCTA } from '../components/CTASection';
import FAQSection from '../components/FAQSection';
import RichText from '../components/RichText';
import { Reveal } from '../components/Reveal';
import { BlogCard } from '../components/cards';
import { getPost, getRelatedPosts } from '../data/blog';
import { SITE } from '../data/site';
import NotFoundPage from './NotFoundPage';

const BlogPostPage = () => {
  const { slug } = useParams();
  const post = getPost(slug);
  if (!post) return <NotFoundPage />;
  const related = getRelatedPosts(slug);
  const mid = Math.ceil(post.content.length / 2);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.metaDescription,
    image: post.image.src,
    author: { '@type': 'Organization', name: SITE.name },
    publisher: { '@type': 'Organization', name: SITE.name },
    mainEntityOfPage: `${SITE.baseUrl}/blog/${post.slug}/`,
  };
  const schemas = [articleSchema, breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Blog', path: '/blog/' }, { name: post.title, path: `/blog/${post.slug}/` }])];
  if (post.faqs?.length) schemas.push(faqSchema(post.faqs));

  return (
    <>
      <SEO
        title={post.metaTitle}
        description={post.metaDescription}
        path={`/blog/${post.slug}/`}
        image={post.image.src}
        jsonLd={schemas}
      />
      <article className="pt-32 md:pt-40">
        <div className="container-site">
          <Reveal className="max-w-3xl">
            <Link to="/blog/" data-testid="post-back-link" className="mb-8 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-muted2 transition-colors hover:text-bronze">
              <ArrowLeft className="h-3.5 w-3.5" /> All Articles
            </Link>
            <div className="flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-bronze">
              <span>{post.category}</span>
              <span className="text-muted2/60">·</span>
              <span className="text-muted2">{post.readTime}</span>
            </div>
            <h1 className="mt-4 font-serif text-4xl font-medium leading-[1.05] tracking-tight text-ink sm:text-5xl">{post.title}</h1>
            <p className="mt-5 text-sm text-muted2">By the {SITE.name} team · {post.date}</p>
          </Reveal>
          <Reveal delay={0.15} className="mt-10">
            <div className="img-frame aspect-[16/8] border border-line p-3">
              <img src={post.image.src} alt={post.image.alt} fetchpriority="high" />
            </div>
          </Reveal>

          <div className="mx-auto max-w-3xl py-16 md:py-20">
            <RichText blocks={post.content.slice(0, mid)} />
            {!post.hasInlineCtas && <InlineCTA />}
            <RichText blocks={post.content.slice(mid)} />
            {post.faqs?.length > 0 && (
              <div className="mt-14 border-t border-line pt-10">
                <FAQSection faqs={post.faqs} heading="Frequently asked questions" />
              </div>
            )}
          </div>
        </div>
      </article>

      <section className="border-t border-line bg-paper2/50 py-16 md:py-20">
        <div className="container-site">
          <div className="flex items-end justify-between gap-6">
            <h2 className="font-serif text-3xl font-medium tracking-tight text-ink">Keep reading</h2>
            <Link to="/blog/" data-testid="related-all-link" className="link-underline hidden text-sm uppercase tracking-[0.2em] sm:inline-flex">
              All Articles <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((p) => <BlogCard key={p.slug} post={p} />)}
          </div>
        </div>
      </section>
      <CTASection testId="post-cta" />
    </>
  );
};

export default BlogPostPage;
